import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import { fmtMoney, fmtDate } from "@/lib/helpers";
import { FilePlus2, FileStack, Package, ArrowRight, Layers } from "lucide-react";

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="p-6 border-r border-b border-border last:border-r-0">
      <div className="flex items-center justify-between">
        <span className="label-tech text-muted-foreground">{label}</span>
        <Icon className="h-4 w-4 text-brand" />
      </div>
      <div className="mt-3 font-heading text-3xl font-bold tracking-tight">{value}</div>
    </div>
  );
}

export default function BuyerDashboard() {
  const { user } = useAuth();
  const [rfqs, setRfqs] = useState([]);
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    api.get("/rfqs").then((r) => setRfqs(r.data)).catch(() => {});
    api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
  }, []);

  const openRfqs = rfqs.filter((r) => r.status !== "ordered").length;
  const activeOrders = orders.filter((o) => !["completed", "delivered"].includes(o.status)).length;

  return (
    <div className="p-6 md:p-8 max-w-7xl">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-tech text-muted-foreground mb-1">Buyer overview</div>
          <h1 className="text-3xl font-bold tracking-tight">Hi, {user?.name?.split(" ")[0]}</h1>
        </div>
        <Link to="/buyer/new-rfq" data-testid="new-rfq-button" className="inline-flex items-center gap-2 px-5 py-2.5 bg-brand text-white rounded-sm hover:bg-brand-dark transition-colors">
          <FilePlus2 className="h-4 w-4" /> New RFQ
        </Link>
      </div>

      <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 border-t border-l border-border rounded-sm overflow-hidden bg-card">
        <Stat label="Total RFQs" value={rfqs.length} icon={FileStack} />
        <Stat label="Awaiting quotes" value={openRfqs} icon={Layers} />
        <Stat label="Active orders" value={activeOrders} icon={Package} />
        <Stat label="All orders" value={orders.length} icon={Package} />
      </div>

      {/* Recent RFQs */}
      <div className="mt-10">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">Recent RFQs</h2>
          <Link to="/buyer/rfqs" className="text-sm text-brand hover:underline flex items-center gap-1">View all <ArrowRight className="h-3.5 w-3.5" /></Link>
        </div>
        {rfqs.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="border border-border rounded-sm overflow-hidden bg-card">
            {rfqs.slice(0, 5).map((r) => (
              <Link key={r.id} to={`/rfqs/${r.id}`} data-testid={`rfq-row-${r.id}`} className="flex items-center gap-4 px-5 py-4 border-b border-border last:border-0 hover:bg-secondary/50 transition-colors">
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{r.title}</div>
                  <div className="text-xs font-mono text-muted-foreground mt-0.5">{r.process} · {r.material} · Qty {r.quantity}</div>
                </div>
                <div className="hidden sm:block text-sm font-mono">{r.has_customer_quote ? "Quote ready" : "In review"}</div>
                <div className="hidden md:block text-sm text-muted-foreground w-24">{fmtDate(r.created_at)}</div>
                <StatusBadge status={r.status} />
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Recent orders */}
      {orders.length > 0 && (
        <div className="mt-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Recent orders</h2>
            <Link to="/buyer/orders" className="text-sm text-brand hover:underline flex items-center gap-1">View all <ArrowRight className="h-3.5 w-3.5" /></Link>
          </div>
          <div className="border border-border rounded-sm overflow-hidden bg-card">
            {orders.slice(0, 4).map((o) => (
              <Link key={o.id} to={`/orders/${o.id}`} data-testid={`order-row-${o.id}`} className="flex items-center gap-4 px-5 py-4 border-b border-border last:border-0 hover:bg-secondary/50 transition-colors">
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{o.rfq_title}</div>
                  <div className="text-xs font-mono text-muted-foreground mt-0.5">{o.supplier_name}</div>
                </div>
                <div className="text-sm font-mono">{fmtMoney(o.total_price)}</div>
                <StatusBadge status={o.status} />
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="border border-dashed border-border rounded-sm p-12 text-center bg-card">
      <FileStack className="h-8 w-8 text-muted-foreground mx-auto" />
      <p className="mt-3 text-sm text-muted-foreground">No RFQs yet. Create your first request for quote.</p>
      <Link to="/buyer/new-rfq" className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-brand text-white rounded-sm hover:bg-brand-dark transition-colors text-sm">
        <FilePlus2 className="h-4 w-4" /> New RFQ
      </Link>
    </div>
  );
}
